import './player-count-upsell.scss';

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider } from 'antd';
import Text from 'antd/lib/typography/Text';

import ZLButton, { ButtonType, ButtonSize } from '../zl-button/zl-button';
import Currency from '../currency/currency';

export interface IPlayerCountUpsellProps {
    currentPlayers: number;
    minimumPlayers: number;
    maximumPlayers: number;
    locale: string;
    onAccept: (newPlayerCount: number) => boolean;
    variant: 'inline' | 'drawer';
    accepted: boolean;
    ticketPrice?: number;
    currencyCode?: string;
    loading?: boolean;
    onExpand?: () => void;
}

const PlayerCountUpsell: React.FunctionComponent<IPlayerCountUpsellProps> = (props) => {
    const { t } = useTranslation();
    const [selectedCount, setSelectedCount] = useState<number>(props.currentPlayers);
    const [collapsed, setCollapsed] = useState<boolean>(props.accepted);

    useEffect(() => {
        setCollapsed(props.accepted);
    }, [props.accepted]);

    useEffect(() => {
        setSelectedCount(props.currentPlayers);
    }, [props.currentPlayers]);

    const isPromoActive = props.currentPlayers >= props.minimumPlayers;
    const hasChanged = selectedCount !== props.currentPlayers;
    const totalPrice = props.ticketPrice != null ? selectedCount * props.ticketPrice : undefined;

    const headerText = isPromoActive
        ? t('playerCountUpsell.headers.promoActive')
        : t('playerCountUpsell.headers.prePromo', { threshold: props.minimumPlayers });

    const sliderMarks = useMemo(() => {
        const marks: Record<number, React.ReactNode> = {};
        for (let i = 0; i <= props.maximumPlayers; i++) {
            if (i === props.minimumPlayers) {
                marks[i] = (
                    <strong>&gt; {i}</strong>
                );
            } else if (i >= props.minimumPlayers) {
                marks[i] = (
                    <strong>{i}</strong>
                );
            } else {
                marks[i] = String(i);
            }
        }
        return marks;
    }, [props.minimumPlayers, props.maximumPlayers]);

    const handleAccept = () => {
        const accepted = props.onAccept(selectedCount);
        if (accepted) {
            setCollapsed(true);
        }
    };

    const stateClass = isPromoActive ? 'player-count-upsell--promo-active' : 'player-count-upsell--pre-promo';

    if (collapsed) {
        return (
            <div
                className={`player-count-upsell player-count-upsell--${props.variant} player-count-upsell--promo-active player-count-upsell--collapsed`}
                data-testid="upsell-card"
                onClick={() => {
                    setCollapsed(false);
                    props.onExpand?.();
                }}
            >
                <div className="upsell-header" data-testid="upsell-header">
                    <Text strong className="upsell-header__text">
                        {t('playerCountUpsell.headers.promoActive')}
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`player-count-upsell player-count-upsell--${props.variant} ${stateClass}`}
            data-testid="upsell-card"
        >
            {/* Header strip */}
            <div className="upsell-header" data-testid="upsell-header">
                <Text strong className="upsell-header__text">
                    {headerText}
                </Text>
            </div>

            {/* Body: slider + disclaimer */}
            <div className="upsell-body">
                <Text className="upsell-body__label">{t('playerCountUpsell.labels.sliderLabel')}</Text>
                <div className="upsell-body__slider" data-testid="upsell-slider">
                    <Slider
                        min={0}
                        max={props.maximumPlayers}
                        marks={sliderMarks}
                        value={selectedCount}
                        onChange={setSelectedCount}
                        tooltip={{ formatter: (val) => t('playerCountUpsell.labels.tooltipPlayers', { count: val }) }}
                        disabled={props.loading}
                    />
                </div>
                <Text className="upsell-body__disclaimer">{t('playerCountUpsell.labels.disclaimer')}</Text>
            </div>

            {/* Footer: total + update button */}
            <div className="upsell-footer">
                <div className="upsell-footer__info">
                    <Text className="upsell-footer__label">{t('playerCountUpsell.labels.footerLabel')}</Text>
                    <div className="upsell-footer__count-group">
                        <Text className="upsell-footer__count" data-testid="upsell-player-count">
                            {selectedCount}
                        </Text>
                        <Text className="upsell-footer__players">
                            {selectedCount === 1 ? t('playerCountUpsell.labels.player') : t('playerCountUpsell.labels.player_plural')}
                        </Text>
                    </div>
                    {totalPrice != null && props.currencyCode && (
                        <span className="upsell-footer__price-group">
                            <Text className="upsell-footer__price-label">{t('playerCountUpsell.labels.totalLabel')}</Text>
                            <Currency
                                value={totalPrice}
                                currency={props.currencyCode}
                                locale={props.locale}
                                className="upsell-footer__price"
                            />
                        </span>
                    )}
                </div>
                {hasChanged && (
                    <ZLButton
                        type={ButtonType.Primary}
                        size={ButtonSize.Large}
                        onClick={handleAccept}
                        buttonId="upsell-update-btn"
                        block={true}
                        loading={props.loading}
                        disabled={props.loading}
                    >
                        {t('playerCountUpsell.labels.updateButton')}
                    </ZLButton>
                )}
            </div>
        </div>
    );
};

export default PlayerCountUpsell;
